import type { Moneda } from '@mc/comercio'
import { ArrowDown, ArrowUp, Copy, GitBranch, GripVertical, Plus, Trash2 } from 'lucide-react'
import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
  type KeyboardEvent,
} from 'react'

import { BotonDeIcono, clases } from '../componentes/ui'
import { esDe, type Errores } from '../motor/errores'
import { arrastreActual, empezarArrastre, terminarArrastre } from './arrastre'
import { CampoDelFormulario } from './CampoDelFormulario'
import { type Bloque, CLASE_DE_COLUMNAS, columnasDe, filasPorAncho } from './catalogo'
import { claveDeFila, type Fila } from './estado'
import { IconoDeTipo } from './iconos'
import { camposDe } from './previa'

/**
 * El lienzo del constructor: **el formulario tal como se verá**, campo a campo, con los
 * anchos lado a lado como quedarán en la web (en un contenedor estrecho, todo entero). Cada
 * campo se elige para cambiar sus ajustes, y al pasar por encima enseña su barra: mover,
 * añadir debajo, duplicar y quitar. Entre fila y fila, un «+» abre el buscador de campos.
 *
 * Los campos se arrastran para reordenarlos, y los tipos de la paleta se arrastran al hueco
 * donde se quieran; una línea dice dónde caerán. Con teclado: los botones «Subir» y «Bajar»
 * de cada campo, o Alt + flecha arriba / abajo sobre él.
 */
type Borde = 'arriba' | 'abajo' | 'izquierda' | 'derecha' | 'final'
type Objetivo = { indice: number; borde: Borde }

type Props = {
  campos: Fila[]
  bloques: Bloque[]
  seleccion: null | number
  errores: Errores
  sitio: number
  moneda: Moneda
  estiloDeMarca: CSSProperties
  soloLectura: boolean
  /** La clave del campo que debe recibir el foco (después de moverlo con el teclado). */
  enfocar: null | { clave: string; vez: number }
  alElegir: (indice: number) => void
  alMover: (desde: number, hueco: number) => void
  alDesplazar: (indice: number, paso: -1 | 1) => void
  alDuplicar: (indice: number) => void
  alQuitar: (indice: number) => void
  alInsertar: (tipo: string, posicion: number) => void
  /** Abre el buscador de campos para añadir uno en esa posición. */
  pedirCampo: (posicion: number) => void
}

const huecoDe = (o: Objetivo) =>
  o.borde === 'arriba' || o.borde === 'izquierda' ? o.indice : o.indice + 1

const INDICADOR: Record<Borde, string> = {
  arriba: '-top-2.5 inset-x-2 h-1',
  abajo: '-bottom-2.5 inset-x-2 h-1',
  izquierda: '-left-2.5 inset-y-2 w-1',
  derecha: '-right-2.5 inset-y-2 w-1',
  final: '',
}

export const Lienzo = (p: Props) => {
  const [objetivo, setObjetivo] = useState<null | Objetivo>(null)
  const [anuncio, setAnuncio] = useState('')
  const raiz = useRef<HTMLDivElement>(null)
  const filas = filasPorAncho(p.campos)
  const campos = camposDe(p.campos)
  const etiquetaDe = (clave: string) => campos.find((c) => c.clave === clave)?.etiqueta || clave
  const hayPasos = p.campos.some((c) => c.blockType === 'paso')

  // Después de mover con el teclado, el foco sigue al campo movido.
  useEffect(() => {
    if (!p.enfocar) return
    raiz.current
      ?.querySelector<HTMLElement>(`[data-campo-boton="${CSS.escape(p.enfocar.clave)}"]`)
      ?.focus()
  }, [p.enfocar])

  const soltar = (e: DragEvent, destino: null | Objetivo) => {
    const arrastre = arrastreActual()
    setObjetivo(null)
    terminarArrastre()
    if (!arrastre || !destino || p.soloLectura) return
    e.preventDefault()
    const hueco = huecoDe(destino)
    if (arrastre.que === 'campo') p.alMover(arrastre.indice, hueco)
    else p.alInsertar(arrastre.tipo, hueco)
  }

  const sobreUnCampo = (e: DragEvent<HTMLDivElement>, indice: number, columnas: number) => {
    const arrastre = arrastreActual()
    if (!arrastre || p.soloLectura) return
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = arrastre.que === 'campo' ? 'move' : 'copy'
    const caja = e.currentTarget.getBoundingClientRect()
    const fila = e.currentTarget.parentElement?.getBoundingClientRect()
    // Lado a lado (en una fila con otros, y con sitio para ello): izquierda o derecha. Si no, arriba o abajo.
    const ladoALado = columnas < 6 && fila !== undefined && caja.width < fila.width * 0.9
    const borde: Borde = ladoALado
      ? e.clientX < caja.left + caja.width / 2
        ? 'izquierda'
        : 'derecha'
      : e.clientY < caja.top + caja.height / 2
        ? 'arriba'
        : 'abajo'
    const siguiente = { indice, borde }
    // Soltar un campo en su propio sitio no hace nada: no se marca.
    if (
      arrastre.que === 'campo' &&
      (huecoDe(siguiente) === arrastre.indice || huecoDe(siguiente) === arrastre.indice + 1)
    ) {
      if (objetivo) setObjetivo(null)
      return
    }
    if (objetivo?.indice !== indice || objetivo.borde !== borde) setObjetivo(siguiente)
  }

  const sobreElFinal = (e: DragEvent) => {
    const arrastre = arrastreActual()
    if (!arrastre || p.soloLectura) return
    e.preventDefault()
    e.dataTransfer.dropEffect = arrastre.que === 'campo' ? 'move' : 'copy'
    if (arrastre.que === 'campo' && arrastre.indice === p.campos.length - 1) return
    if (objetivo?.borde !== 'final') setObjetivo({ indice: p.campos.length - 1, borde: 'final' })
  }

  const teclas = (e: KeyboardEvent, indice: number, etiqueta: string) => {
    if (p.soloLectura || !e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return
    e.preventDefault()
    const paso = e.key === 'ArrowUp' ? -1 : 1
    const destino = indice + paso
    if (destino < 0 || destino >= p.campos.length) return
    p.alDesplazar(indice, paso)
    setAnuncio(`«${etiqueta}» ahora es el campo ${destino + 1} de ${p.campos.length}.`)
  }

  let numeroDePaso = 1
  return (
    <div
      className="@container"
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setObjetivo(null)
      }}
      ref={raiz}
    >
      <p aria-live="polite" className="sr-only">
        {anuncio}
      </p>
      <div
        className="mx-auto max-w-[46rem] rounded-caja border border-linea bg-papel px-4 py-6 shadow-suave sm:px-8 sm:py-8"
        style={p.estiloDeMarca}
      >
        {hayPasos && p.campos[0]?.blockType !== 'paso' && (
          <SeparadorDePaso etiqueta={null} numero={1} />
        )}
        {p.campos.length === 0 && (
          <div
            className={clases(
              'flex flex-col items-center gap-3 rounded-xl border-2 border-dashed px-6 py-12 text-center',
              objetivo?.borde === 'final'
                ? 'border-violeta bg-violeta-suave/40'
                : 'border-linea-fuerte',
            )}
            onDragOver={sobreElFinal}
            onDrop={(e) => soltar(e, { indice: -1, borde: 'final' })}
          >
            <p className="font-display text-lg font-semibold">Tu formulario está vacío</p>
            <p className="max-w-sm text-sm text-tenue">
              Añade el primer campo: pulsa un tipo de la derecha, arrástralo hasta aquí, o usa el
              botón.
            </p>
            {!p.soloLectura && (
              <button
                className="mt-1 inline-flex h-9 items-center gap-1.5 rounded-boton bg-violeta px-3.5 text-sm font-medium text-white shadow-suave hover:bg-violeta-2"
                onClick={() => p.pedirCampo(0)}
                type="button"
              >
                <Plus aria-hidden className="size-4" /> Añadir un campo
              </button>
            )}
          </div>
        )}
        {filas.map((indices, f) => (
          <Fragment key={claveDeFila(p.campos[indices[0]!]!, indices[0]!)}>
            {!p.soloLectura && (
              <Insertador posicion={indices[0]!} pedirCampo={p.pedirCampo} primero={f === 0} />
            )}
            <div className="grid grid-cols-6 gap-x-4 gap-y-5">
              {indices.map((i) => {
                const fila = p.campos[i]!
                const campo = campos[i]!
                const clave = claveDeFila(fila, i)
                const columnas = columnasDe(fila)
                const bloque = p.bloques.find((b) => b.slug === fila.blockType)
                const tipo = bloque?.etiqueta ?? String(fila.blockType ?? 'Campo')
                const etiqueta = String(
                  fila.etiqueta ||
                    (fila.blockType === 'informativo' ? 'Texto informativo' : '') ||
                    fila.clave ||
                    tipo,
                )
                const error = Object.entries(p.errores).find(([ruta]) =>
                  esDe(ruta, `campos.${i}`),
                )?.[1]
                const reglas = ((fila.condicion as null | { reglas?: unknown[] })?.reglas ?? [])
                  .length
                const elegido = p.seleccion === i
                if (fila.blockType === 'paso') numeroDePaso += 1
                return (
                  <div
                    className={clases(
                      'group/campo relative rounded-xl border transition-colors',
                      fila.blockType === 'paso' ? 'px-3 py-1.5' : 'px-3 py-2.5 sm:px-4',
                      CLASE_DE_COLUMNAS[columnas],
                      elegido
                        ? 'border-violeta bg-violeta-suave/25 ring-3 ring-violeta/10'
                        : error
                          ? 'border-error/50 bg-error-suave/40'
                          : 'border-transparent hover:border-linea-fuerte hover:bg-papel-2/50',
                    )}
                    data-campo={i}
                    draggable={!p.soloLectura}
                    key={clave}
                    onDragEnd={() => {
                      terminarArrastre()
                      setObjetivo(null)
                    }}
                    onDragOver={(e) => sobreUnCampo(e, i, columnas)}
                    onDragStart={(e) => empezarArrastre(e, { que: 'campo', indice: i })}
                    onDrop={(e) => soltar(e, objetivo)}
                  >
                    {/* Elegir el campo: un botón que lo cubre entero, con su nombre para quien no ve el lienzo. */}
                    <button
                      aria-label={`${etiqueta} · ${tipo}. Editar sus ajustes${p.soloLectura ? '' : '; Alt y las flechas lo mueven'}.`}
                      aria-pressed={elegido}
                      className="absolute inset-0 z-[1] cursor-pointer rounded-xl"
                      data-campo-boton={clave}
                      onClick={() => p.alElegir(i)}
                      onKeyDown={(e) => teclas(e, i, etiqueta)}
                      type="button"
                    />
                    <div className="pointer-events-none select-none" inert>
                      {fila.blockType === 'paso' ? (
                        <SeparadorDePaso
                          etiqueta={String(fila.etiqueta ?? '')}
                          numero={numeroDePaso}
                        />
                      ) : (
                        <CampoDelFormulario
                          campo={campo}
                          etiquetaDe={etiquetaDe}
                          id={`lienzo-${i}`}
                          modo="muestra"
                          moneda={p.moneda}
                          sitio={p.sitio}
                        />
                      )}
                      {(reglas > 0 || error) && (
                        <p className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                          {reglas > 0 && (
                            <span className="inline-flex items-center gap-1 rounded-pastilla border border-violeta-borde bg-violeta-suave px-1.5 py-0.5 font-medium text-violeta">
                              <GitBranch aria-hidden className="size-3" />{' '}
                              {reglas === 1
                                ? 'Sale solo si se cumple su condición'
                                : `Sale según ${reglas} condiciones`}
                            </span>
                          )}
                          {error && <span className="font-medium text-error">{error}</span>}
                        </p>
                      )}
                    </div>

                    {/* La etiqueta del tipo y la barra de acciones: al pasar por encima, con el foco dentro o elegido. */}
                    <span
                      aria-hidden
                      className={clases(
                        'pointer-events-none absolute -top-2.5 left-3 z-[2] inline-flex items-center gap-1 rounded-pastilla border px-1.5 py-px text-[11px] font-medium transition-opacity',
                        elegido
                          ? 'border-violeta bg-violeta text-white opacity-100'
                          : 'border-linea-fuerte bg-papel text-tinta-2 opacity-0 group-focus-within/campo:opacity-100 group-hover/campo:opacity-100',
                      )}
                    >
                      <IconoDeTipo className="size-3" tipo={String(fila.blockType)} />
                      {tipo}
                    </span>
                    {!p.soloLectura && (
                      <div
                        className={clases(
                          'absolute -top-4 right-2 z-[2] flex items-center gap-0.5 rounded-boton border border-linea bg-papel p-0.5 shadow-media transition-opacity',
                          elegido
                            ? 'opacity-100'
                            : 'opacity-0 group-focus-within/campo:opacity-100 group-hover/campo:opacity-100 pointer-coarse:hidden',
                        )}
                        role="toolbar"
                        aria-label={`Acciones de «${etiqueta}»`}
                      >
                        <span
                          aria-hidden
                          className="flex size-7 cursor-grab items-center justify-center text-tenue active:cursor-grabbing"
                          title="Arrastra para mover"
                        >
                          <GripVertical className="size-4" />
                        </span>
                        <BotonDeIcono
                          disabled={i === 0}
                          etiqueta="Subir"
                          onClick={() => p.alDesplazar(i, -1)}
                          pequeno
                        >
                          <ArrowUp />
                        </BotonDeIcono>
                        <BotonDeIcono
                          disabled={i === p.campos.length - 1}
                          etiqueta="Bajar"
                          onClick={() => p.alDesplazar(i, 1)}
                          pequeno
                        >
                          <ArrowDown />
                        </BotonDeIcono>
                        <BotonDeIcono
                          etiqueta="Añadir un campo debajo"
                          onClick={() => p.pedirCampo(i + 1)}
                          pequeno
                        >
                          <Plus />
                        </BotonDeIcono>
                        <BotonDeIcono etiqueta="Duplicar" onClick={() => p.alDuplicar(i)} pequeno>
                          <Copy />
                        </BotonDeIcono>
                        <BotonDeIcono
                          etiqueta="Quitar"
                          onClick={() => p.alQuitar(i)}
                          pequeno
                          tipo="peligro"
                        >
                          <Trash2 />
                        </BotonDeIcono>
                      </div>
                    )}
                    {objetivo?.indice === i && objetivo.borde !== 'final' && (
                      <span
                        aria-hidden
                        className={clases(
                          'pointer-events-none absolute z-[3] rounded-full bg-violeta',
                          INDICADOR[objetivo.borde],
                        )}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          </Fragment>
        ))}

        {p.campos.length > 0 && !p.soloLectura && (
          <div
            className="mt-5"
            onDragOver={sobreElFinal}
            onDrop={(e) => soltar(e, objetivo?.borde === 'final' ? objetivo : null)}
          >
            <button
              className={clases(
                'flex w-full items-center justify-center gap-1.5 rounded-xl border-2 border-dashed py-3 text-sm font-medium transition-colors',
                objetivo?.borde === 'final'
                  ? 'border-violeta bg-violeta-suave/40 text-violeta'
                  : 'border-linea-fuerte text-tenue hover:border-violeta hover:text-violeta',
              )}
              onClick={() => p.pedirCampo(p.campos.length)}
              type="button"
            >
              <Plus aria-hidden className="size-4" /> Añadir un campo al final
            </button>
          </div>
        )}

        {p.campos.length > 0 && (
          <div className="mt-6 flex items-center gap-3" inert>
            <span className="inline-flex h-11 items-center rounded-lg bg-(--marca) px-6 text-[15px] font-medium text-(--texto-marca) shadow-suave">
              Enviar
            </span>
            <span className="text-xs text-tenue">El botón de enviar lo pone tu web.</span>
          </div>
        )}
      </div>
    </div>
  )
}

const Insertador = ({
  posicion,
  pedirCampo,
  primero,
}: {
  pedirCampo: (posicion: number) => void
  posicion: number
  primero: boolean
}) => (
  <div
    className={clases(
      'group/insertar relative flex items-center justify-center',
      primero ? 'h-2' : 'h-3',
    )}
  >
    <span
      aria-hidden
      className="pointer-events-none absolute inset-x-6 top-1/2 h-px bg-transparent transition-colors group-hover/insertar:bg-violeta-borde"
    />
    <button
      aria-label={`Añadir un campo aquí (en el puesto ${posicion + 1})`}
      className="absolute top-1/2 left-1/2 z-[4] flex size-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-violeta-borde bg-papel text-violeta opacity-0 shadow-suave transition-opacity group-hover/insertar:opacity-100 hover:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
      onClick={() => pedirCampo(posicion)}
      title="Añadir un campo aquí"
      type="button"
    >
      <Plus aria-hidden className="size-3.5" />
    </button>
  </div>
)

const SeparadorDePaso = ({ numero, etiqueta }: { etiqueta: null | string; numero: number }) => (
  <div className="flex items-center gap-3 py-1">
    <span aria-hidden className="h-px flex-1 bg-linea-fuerte" />
    <span className="rounded-full border border-linea-fuerte bg-papel-2 px-3 py-1 text-xs font-semibold text-tinta-2">
      Paso {numero}
      {etiqueta ? ` · ${etiqueta}` : ''}
    </span>
    <span aria-hidden className="h-px flex-1 bg-linea-fuerte" />
  </div>
)
